import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/format-bytes';
import {
  type CreateResourceHostRequest,
  CreateResourceHostRequestSchema,
  type ResourceHost,
  type ResourceHostKind,
  type TestResourceHostConnectionResponse,
  type UpdateResourceHostRequest,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  Pause,
  PencilLine,
  Plus,
  Trash2,
} from 'lucide-react';
import { type FormEvent, useState } from 'react';

const STATUS_LABEL: Record<ResourceHost['status'], { text: string; tone: string }> = {
  ok: { text: '정상', tone: 'text-success' },
  error: { text: '오류', tone: 'text-destructive' },
  disabled: { text: '비활성', tone: 'text-foreground-muted' },
  pending: { text: '대기', tone: 'text-foreground-muted' },
};

const STATUS_ICON: Record<ResourceHost['status'], typeof Activity> = {
  ok: CheckCircle2,
  error: AlertTriangle,
  disabled: Pause,
  pending: Activity,
};

const KIND_LABEL: Record<ResourceHostKind, string> = {
  remote: '원격 (HTTP)',
  local: '동일 호스트',
};

export function AdminResourceHosts() {
  const qc = useQueryClient();
  const hostsQuery = useQuery({
    queryKey: ['admin', 'resource-hosts'],
    queryFn: () => api.adminListResourceHosts(),
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ResourceHost | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.adminDeleteResourceHost(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'resource-hosts'] }),
  });

  return (
    <PageShell
      header={
        <PageHeader
          title="리소스 호스트"
          description="대상 호스트에서 띄운 resource-collector 의 HTTP 엔드포인트로부터 GPU·CPU·디스크 사용량을 수집합니다. 동일 호스트는 'local' 로 등록하면 collector 프로세스 없이 in-process 로 수집합니다."
          actions={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> 호스트 추가
            </Button>
          }
        />
      }
    >
      {hostsQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : hostsQuery.error ? (
        <p className="text-sm text-destructive">
          호스트 목록을 불러올 수 없습니다:{' '}
          {hostsQuery.error instanceof ApiError ? hostsQuery.error.message : '알 수 없는 오류'}
        </p>
      ) : !hostsQuery.data || hostsQuery.data.hosts.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center">
          <p className="text-sm text-foreground-muted">
            등록된 호스트가 없습니다. 우상단 "호스트 추가" 로 collector URL/토큰을 등록하거나 local
            호스트로 등록하세요.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40">
              <tr className="text-left text-xs text-foreground-muted">
                <th className="px-4 py-3 font-medium">이름</th>
                <th className="px-4 py-3 font-medium">종류</th>
                <th className="px-4 py-3 font-medium">Collector URL</th>
                <th className="px-4 py-3 font-medium">주기</th>
                <th className="px-4 py-3 font-medium">마운트</th>
                <th className="px-4 py-3 font-medium">상태</th>
                <th className="px-4 py-3 font-medium">최종 수집</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {hostsQuery.data.hosts.map((host) => {
                const Icon = STATUS_ICON[host.status];
                const tone = STATUS_LABEL[host.status];
                return (
                  <tr key={host.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{host.name}</td>
                    <td className="px-4 py-3 text-foreground-muted">{KIND_LABEL[host.kind]}</td>
                    <td className="px-4 py-3 text-foreground-muted font-mono text-xs break-all">
                      {host.kind === 'remote' ? host.collectorUrl : '—'}
                    </td>
                    <td className="px-4 py-3 text-foreground-muted">{host.sampleIntervalSec}s</td>
                    <td className="px-4 py-3 text-foreground-muted">{host.diskMountPath}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 ${tone.tone}`}>
                        <Icon className="size-3.5" />
                        {tone.text}
                      </span>
                      {host.lastError ? (
                        <p
                          className="mt-1 text-xs text-destructive line-clamp-2"
                          title={host.lastError}
                        >
                          {host.lastError}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-foreground-muted text-xs">
                      {host.lastSeenAt ? new Date(host.lastSeenAt).toLocaleString('ko-KR') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditTarget(host)}
                          aria-label="수정"
                        >
                          <PencilLine className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                `"${host.name}" 호스트를 삭제하시겠습니까? 수집을 중단하고 히스토리도 사라집니다.`,
                              )
                            ) {
                              deleteMut.mutate(host.id);
                            }
                          }}
                          aria-label="삭제"
                        >
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateHostModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {editTarget ? (
        <EditHostModal host={editTarget} open={!!editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
    </PageShell>
  );
}

// ───────────────────────────────────────────────────────────────
// 등록 모달
// ───────────────────────────────────────────────────────────────

function CreateHostModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ResourceHostKind>('remote');
  const [collectorUrl, setCollectorUrl] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [sampleIntervalSec, setSampleIntervalSec] = useState('5');
  const [diskMountPath, setDiskMountPath] = useState('/');
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResourceHostConnectionResponse | null>(null);

  const reset = () => {
    setName('');
    setKind('remote');
    setCollectorUrl('');
    setAuthToken('');
    setSampleIntervalSec('5');
    setDiskMountPath('/');
    setError(null);
    setTestResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const collectBody = (): CreateResourceHostRequest => {
    const base = {
      name: name.trim(),
      kind,
      sampleIntervalSec: Number.parseInt(sampleIntervalSec, 10) || 5,
      diskMountPath: diskMountPath.trim() || '/',
      enabled: true,
    } as const;
    if (kind === 'remote') {
      return {
        ...base,
        collectorUrl: collectorUrl.trim(),
        authToken,
      } as CreateResourceHostRequest;
    }
    return base as CreateResourceHostRequest;
  };

  const testMut = useMutation({
    mutationFn: () =>
      api.adminTestResourceHostConnection({
        kind,
        collectorUrl: kind === 'remote' ? collectorUrl.trim() : undefined,
        authToken: kind === 'remote' ? authToken : undefined,
        diskMountPath: diskMountPath.trim() || '/',
      }),
    onSuccess: (res) => setTestResult(res),
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : '연결 실패';
      setTestResult({ ok: false, latencyMs: 0, summary: null, error: msg });
    },
  });

  const createMut = useMutation({
    mutationFn: () => {
      const parsed = CreateResourceHostRequestSchema.safeParse(collectBody());
      if (!parsed.success) {
        const first = parsed.error.issues[0]?.message ?? '입력값을 확인하세요.';
        throw new Error(first);
      }
      return api.adminCreateResourceHost(parsed.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'resource-hosts'] });
      handleClose();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : '저장 실패',
      );
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    createMut.mutate();
  };

  return (
    <Modal open={open} onClose={handleClose} title="호스트 추가" size="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="이름" htmlFor="rh-name">
          <Input
            id="rh-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="AI 서버 1"
            required
            autoFocus
          />
        </Field>
        <Field label="호스트 종류" htmlFor="rh-kind">
          <select
            id="rh-kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as ResourceHostKind);
              setTestResult(null);
            }}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="remote">원격 (다른 호스트의 resource-collector 폴링)</option>
            <option value="local">동일 호스트 (메인 서버 in-process 수집)</option>
          </select>
        </Field>
        {kind === 'remote' ? (
          <>
            <Field
              label="Collector URL"
              htmlFor="rh-url"
              hint="대상 호스트에서 띄운 resource-collector 의 base URL (예: http://10.0.0.5:7234)"
            >
              <Input
                id="rh-url"
                value={collectorUrl}
                onChange={(e) => {
                  setCollectorUrl(e.target.value);
                  setTestResult(null);
                }}
                placeholder="http://10.0.0.5:7234"
                required
              />
            </Field>
            <Field
              label="인증 토큰"
              htmlFor="rh-token"
              hint="collector 의 COLLECTOR_TOKEN 환경변수와 동일한 값"
            >
              <Input
                id="rh-token"
                value={authToken}
                onChange={(e) => {
                  setAuthToken(e.target.value);
                  setTestResult(null);
                }}
                type="password"
                autoComplete="new-password"
                placeholder="최소 8자"
                required
              />
            </Field>
            <CollectorGuide />
          </>
        ) : (
          <p className="text-xs text-foreground-muted rounded-lg border border-border bg-surface-muted/30 px-3 py-2">
            로컬 호스트는 별도 collector 프로세스 없이 메인 서버 프로세스가 직접{' '}
            <code className="font-mono">/proc</code>·<code className="font-mono">nvidia-smi</code>·
            <code className="font-mono">df</code> 를 호출합니다. URL/토큰은 필요 없습니다.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="수집 주기 (초)" htmlFor="rh-interval">
            <Input
              id="rh-interval"
              value={sampleIntervalSec}
              onChange={(e) => setSampleIntervalSec(e.target.value)}
              type="number"
              min={1}
              max={60}
              required
            />
          </Field>
          <Field label="디스크 마운트 경로" htmlFor="rh-mount">
            <Input
              id="rh-mount"
              value={diskMountPath}
              onChange={(e) => {
                setDiskMountPath(e.target.value);
                setTestResult(null);
              }}
              placeholder="/"
              required
            />
          </Field>
        </div>

        <TestResultPanel result={testResult} pending={testMut.isPending} />

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
          >
            {testMut.isPending ? '테스트 중…' : '연결 테스트'}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              취소
            </Button>
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? '저장 중…' : '저장'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────
// 수정 모달 — 토큰은 빈값 유지 시 기존 토큰 보존
// ───────────────────────────────────────────────────────────────

function EditHostModal({
  host,
  open,
  onClose,
}: {
  host: ResourceHost;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(host.name);
  const [kind, setKind] = useState<ResourceHostKind>(host.kind);
  const [collectorUrl, setCollectorUrl] = useState(host.collectorUrl ?? '');
  const [authToken, setAuthToken] = useState('');
  const [sampleIntervalSec, setSampleIntervalSec] = useState(String(host.sampleIntervalSec));
  const [diskMountPath, setDiskMountPath] = useState(host.diskMountPath);
  const [enabled, setEnabled] = useState(host.enabled);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResourceHostConnectionResponse | null>(null);

  const collectBody = (): UpdateResourceHostRequest => {
    const body: UpdateResourceHostRequest = {};
    if (name !== host.name) body.name = name.trim();
    if (kind !== host.kind) body.kind = kind;
    if (collectorUrl !== (host.collectorUrl ?? '')) {
      body.collectorUrl = collectorUrl.trim();
    }
    if (authToken.length > 0) body.authToken = authToken;
    const intervalNum = Number.parseInt(sampleIntervalSec, 10);
    if (intervalNum !== host.sampleIntervalSec) body.sampleIntervalSec = intervalNum;
    if (diskMountPath !== host.diskMountPath) body.diskMountPath = diskMountPath.trim();
    if (enabled !== host.enabled) body.enabled = enabled;
    return body;
  };

  const testMut = useMutation({
    mutationFn: () => api.adminTestResourceHostConnectionById(host.id),
    onSuccess: (res) => setTestResult(res),
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : '연결 실패';
      setTestResult({ ok: false, latencyMs: 0, summary: null, error: msg });
    },
  });

  const updateMut = useMutation({
    mutationFn: () => api.adminUpdateResourceHost(host.id, collectBody()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'resource-hosts'] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : '저장 실패',
      );
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    updateMut.mutate();
  };

  return (
    <Modal open={open} onClose={onClose} title={`호스트 수정 — ${host.name}`} size="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="이름" htmlFor="rh-edit-name">
          <Input
            id="rh-edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Field>
        <Field label="호스트 종류" htmlFor="rh-edit-kind">
          <select
            id="rh-edit-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ResourceHostKind)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="remote">원격 (다른 호스트의 resource-collector 폴링)</option>
            <option value="local">동일 호스트 (메인 서버 in-process 수집)</option>
          </select>
        </Field>
        {kind === 'remote' ? (
          <>
            <Field label="Collector URL" htmlFor="rh-edit-url">
              <Input
                id="rh-edit-url"
                value={collectorUrl}
                onChange={(e) => setCollectorUrl(e.target.value)}
                placeholder="http://10.0.0.5:7234"
                required
              />
            </Field>
            <Field
              label="인증 토큰 (변경 시에만 입력)"
              htmlFor="rh-edit-token"
              hint="비워두면 기존 토큰 유지."
            >
              <Input
                id="rh-edit-token"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                type="password"
                autoComplete="new-password"
                placeholder={
                  host.hasAuthToken ? '기존 토큰 사용 중 — 변경 시에만 입력' : '최소 8자'
                }
              />
            </Field>
            <CollectorGuide />
          </>
        ) : (
          <p className="text-xs text-foreground-muted rounded-lg border border-border bg-surface-muted/30 px-3 py-2">
            로컬 호스트는 메인 서버 프로세스가 직접 메트릭을 수집합니다.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="수집 주기 (초)" htmlFor="rh-edit-interval">
            <Input
              id="rh-edit-interval"
              value={sampleIntervalSec}
              onChange={(e) => setSampleIntervalSec(e.target.value)}
              type="number"
              min={1}
              max={60}
              required
            />
          </Field>
          <Field label="디스크 마운트 경로" htmlFor="rh-edit-mount">
            <Input
              id="rh-edit-mount"
              value={diskMountPath}
              onChange={(e) => setDiskMountPath(e.target.value)}
              required
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>활성화 (체크 해제 시 수집 중단)</span>
        </label>

        <TestResultPanel result={testResult} pending={testMut.isPending} />

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => testMut.mutate()}
            disabled={testMut.isPending}
          >
            {testMut.isPending ? '테스트 중…' : '연결 테스트 (저장된 설정)'}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" disabled={updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────
// 공통 패널
// ───────────────────────────────────────────────────────────────

function TestResultPanel({
  result,
  pending,
}: {
  result: TestResourceHostConnectionResponse | null;
  pending: boolean;
}) {
  if (pending) {
    return <p className="text-sm text-foreground-muted">테스트 중…</p>;
  }
  if (!result) return null;
  if (!result.ok) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        <p className="font-medium">연결 실패</p>
        <p className="text-xs mt-1">{result.error}</p>
      </div>
    );
  }
  const s = result.summary;
  return (
    <div className="rounded-lg border border-success/40 bg-success/5 px-3 py-2 text-sm text-success">
      <p className="font-medium">연결 성공 ({result.latencyMs}ms)</p>
      {s ? (
        <p className="text-xs mt-1 text-foreground-muted">
          GPU {s.gpuCount}개 · CPU {s.cpuCores} 코어 · Memory {formatBytes(s.memoryTotalBytes)} ·
          Disk {formatBytes(s.diskTotalBytes)}
        </p>
      ) : null}
    </div>
  );
}

function CodeBlock({ title, children }: { title: string; children: string }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground">{title}</p>
        <CopyButton text={children} label="명령어 복사" />
      </div>
      <pre className="mt-1 font-mono text-[11px] bg-background border border-border rounded px-2 py-1.5 overflow-x-auto whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

function CollectorGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-surface-muted/30 text-xs">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-surface-muted/50 transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <HelpCircle className="size-3.5" />
          <span>대상 호스트에 resource-collector 를 어떻게 띄우나요?</span>
        </span>
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="px-3 pb-3 pt-1 space-y-3 text-foreground-muted leading-relaxed">
          <p>
            대상 호스트에서 <code className="font-mono">@innermate/resource-collector</code> 의 빌드
            결과물을 Node 로 실행합니다. <code className="font-mono">/proc</code>·
            <code className="font-mono">nvidia-smi</code>·<code className="font-mono">df</code> 접근
            권한만 있으면 충분합니다 (root 불필요).
          </p>
          <div>
            <CodeBlock title="1. 토큰 생성 (예시)">{'openssl rand -hex 24'}</CodeBlock>
            <p className="mt-1">
              생성된 값은 이 폼의 "인증 토큰" 입력란과 대상 호스트의{' '}
              <code className="font-mono">COLLECTOR_TOKEN</code> 환경변수에 동일하게 넣습니다.
            </p>
          </div>
          <div>
            <CodeBlock title="2. collector 부팅 (개발/검증 용)">
              {
                'COLLECTOR_TOKEN=<위에서 만든 값> \\\nCOLLECTOR_PORT=7234 \\\nCOLLECTOR_HOST=0.0.0.0 \\\nCOLLECTOR_ALLOW_MOUNTS=/ \\\n  pnpm --filter @innermate/resource-collector start'
              }
            </CodeBlock>
            <p className="mt-1">
              운영 환경에서는 systemd / pm2 / Docker 등 평소 쓰는 방식으로 데몬화하세요. 자세한 운영
              가이드는 <code className="font-mono">docs/21-admin-resource-usage.md</code> 의 "운영
              가이드" 절을 참고합니다.
            </p>
          </div>
          <div>
            <CodeBlock title="3. 동작 확인">
              {'curl -H "Authorization: Bearer <token>" http://<host>:7234/metrics?mount=/'}
            </CodeBlock>
            <p className="mt-1">
              200 + JSON 응답이 정상입니다. 401 이면 토큰 불일치, 400 이면 마운트 경로 화이트리스트
              미허용, 5xx 면 호스트 내부 명령 실패입니다.
            </p>
          </div>
          <p className="pt-2 border-t border-border">
            저장된 토큰은 <code className="font-mono">INNERMATE_SECRET_KEY</code> 로 AES-256-GCM
            암호화되어 DB 에 보관됩니다. 응답에는 절대 노출되지 않습니다.
          </p>
        </div>
      ) : null}
    </div>
  );
}
